import { getCategoryList } from "./utils/getCategoryList";

export interface StudentRecord {
  created_at: string;
  username: string;
  updated_at: string;
  profile_id: string;
  student_id: number;
  is_admin: boolean;
  pfp_id: number;
  contract_override: boolean;
}

export interface CategoryData {
  category: string;
  completed: number;
  total: number;
  problems: object[];
  question_type: string;
}

export interface AdminSwitch {
    switchLabel: string;
    switchCondition: () => Promise<boolean>;
    switchAction: () => void;
}

export interface IOPair {
    input: any[];
    output: any;
}

export interface EvalResult {
  input: string;
  expected: string;
  actual: string;
  equal: boolean;
  printed: string;
}

export type EvalResponse
    = { status: 'success'; report: EvalResult[] }
    | { status: 'failure'; message: string }

export interface Problem {
    description: string;
    starter?: string;
    meta: {
        name: string;
        title: string;
        difficulty: string;
        author: string;
        category: string;
        question_type: Array<string>;
    };
    io: Array<IOPair>;
    mutations?: Array<string>;
    solution?: string;
}

export type Progress = {
    category: string;
    completed: number;
    total: number;
    question_type: string;
    problems: {
        title: string;
        difficulty: string;
    }[];
}

export type Reflection = {
    category: string;
    problem_title: string;
    code: {
        code: string;
    } | {
        Input: string[];
        Expected: string;
    }[];
    reflection: string | {
        question: string;
        answer: string;
    };
    submitted_at: Date;
}

export type Submission = {
    problem_title: string;
    passed_tests: number;
    total_tests: number;
}

export interface GenericContract {
    gradeWanted: string;
    problemsToSolve: number;
    codeDescription: string;
    reflectionPlan: string;
}

export interface CodingContract {
    gradeWanted: string;
    problemsToSolveByCategory: Record<string, number>;
    codeDescription: string;
    reflectionPlan: string;
}

export type ContractData = {
    Coding: CodingContract;
    Haystack: GenericContract;
    Mutation: GenericContract;
}

const codingCategories = getCategoryList();

export const BLANK_CONTRACT: ContractData = {
    "Coding": {
        gradeWanted: "",
        problemsToSolveByCategory: codingCategories.reduce(
            (acc, cat) => ({ ...acc, [cat]: 0 }),
            {}
        ),
        codeDescription: "",
        reflectionPlan: "",
    },
    "Mutation": { gradeWanted: "", problemsToSolve: 0, codeDescription: '', reflectionPlan: '' },
    "Haystack": { gradeWanted: "", problemsToSolve: 0, codeDescription: '', reflectionPlan: '' }
}

export type ContractProgress = {
    [category: string]: number;
}

export interface QuestionOption {
    label: string;
    value: string;
}

export interface Question {
    id: string;
    text: string;
    type: "radio" | "checkbox" | "number" | "textarea" | "problem_picker";
    options?: QuestionOption[];
    category: string;
    placeholder?: string;
    min?: number;
    max?: number;
    randomizeable?: boolean;
    relies_on?: string;
    condition?: "success" | "struggle";
}

export interface FormAnswers {
    [questionId: string]: string | string[] | number;
}

export interface Session {
    session_id: string;
    profile_id: string;
    started_at: Date;
    ended_at?: Date;
    planned_duration_minutes: number;
    planned_exercise_count: number;
    planned_categories: string[];
    created_at: Date;
    updated_at: Date;
}

export interface SessionReflection {
    reflection_id: string;
    session_id: string;
    reflection_type: "pre" | "post";
    form_data: FormAnswers;
    created_at: Date;
}

export interface BlogPost {
    blog_text: string;
    meta: {
        blog_slug: string;
        editor?: string;
        author: string;
        title: string;
        category: string;
        order?: number;
    };
    questions?: {
        question: string;
        code?: string;
        options: string[];
        correct: string | string[];
        explanation?: string;
    }[];
}

export interface ProblemLog {
  problem_title: string;
  session_id: string;
  profile_id: string;
  problem_category: string;
  question_type: string;
  attempt_count: number;
  successful: boolean;
  passed_tests: number;
  total_tests: number;
  time_spent_seconds: number;
  first_attempt_at: string;
  last_attempt_at: string;
  code: unknown;
}

export interface ProblemSessionStats{
    elapsedTimeSeconds: number;
    completed: boolean;
    passedTests: number;
    totalTests: number;
}

export interface ProblemArgs{
    problemName: string;
    activeSession: boolean;
    problemSessionStats: Record<string, ProblemSessionStats>;
    setProblemSessionStats: React.Dispatch<React.SetStateAction<Record<string, ProblemSessionStats>>>;
    progress?: Pick<Submission, 'problem_title' | 'passed_tests' | 'total_tests'>[];
}

export interface SessionReflectionRecord {
  id: string;
  start_time: string;
  end_time: string | null;
  exercise_goals: number;
  pre_session_reflection: FormAnswers | null;
  post_session_reflection: FormAnswers | null;
}

export interface UserData {
  name: string;
  pfp_id: number;
}